import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import axiosLib from 'axios';
import { attachAuth } from '../services/auth';

// This page talks to /api directly rather than through services/api.ts. It still needs the
// session token on every call, so it gets its own authenticated instance.
const axios = axiosLib.create();
attachAuth(axios);

const Management: React.FC = () => {
  const { theme, isDark } = useTheme();
  const [activeTab, setActiveTab] = useState<'principles' | 'orchestrator' | 'team'>('principles');

  // Orchestrator state
  const [orchestratorStatus, setOrchestratorStatus] = useState<any>(null);
  const [iterationState, setIterationState] = useState<any>(null);

  // Principles state
  const [principles, setPrinciples] = useState<any[]>([]);
  const [principlesState, setPrinciplesState] = useState<any>({});
  const [editingPrinciple, setEditingPrinciple] = useState<any>(null);
  const [newPrinciple, setNewPrinciple] = useState({ title: '', description: '', category: '' });
  const [expandedTeamMembers, setExpandedTeamMembers] = useState<Set<string>>(new Set());
  const [generalInstructions, setGeneralInstructions] = useState<string>('');
  const [editingGeneralInstructions, setEditingGeneralInstructions] = useState<boolean>(false);
  const [continuousExecution, setContinuousExecution] = useState<any>({});
  const [workerRunning, setWorkerRunning] = useState<boolean>(false);
  const [pendingActions, setPendingActions] = useState<Set<string>>(new Set());

  // Team members state
  const [teamMembers, setTeamMembers] = useState<any[]>([]);
  const [newMember, setNewMember] = useState({ name: '', role: '' });
  const [editingMember, setEditingMember] = useState<any>(null);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 5000); // Refresh every 5 seconds
    return () => clearInterval(interval);
  }, [activeTab, editingGeneralInstructions]);

  const loadData = async () => {
    try {
      if (activeTab === 'orchestrator') {
        const [statusRes, stateRes, continuousRes] = await Promise.all([
          axios.get('/api/orchestrator/status'),
          axios.get('/api/orchestrator/iteration-state'),
          axios.get('/api/orchestrator/continuous-execution')
        ]);
        setOrchestratorStatus(statusRes.data);
        setIterationState(stateRes.data);
        setContinuousExecution(continuousRes.data);
        setWorkerRunning(continuousRes.data.worker_running || false);
      } else if (activeTab === 'principles') {
        // Fetch principles with state already merged
        try {
          const [stateRes, instructionsRes, continuousRes] = await Promise.all([
            axios.get('/api/principles/state'),
            axios.get('/api/orchestrator/general-instructions'),
            axios.get('/api/orchestrator/continuous-execution')
          ]);
          const data = stateRes.data || {};

          console.log('Principles API response:', data);
          console.log('Principles array:', data.principles);

          // Ensure principles is always an array
          setPrinciples(Array.isArray(data.principles) ? data.principles : []);
          setPrinciplesState(data);

          console.log('Continuous execution state:', continuousRes.data);
          setContinuousExecution(continuousRes.data);
          setWorkerRunning(continuousRes.data.worker_running || false);

          // Don't update general instructions if user is currently editing them
          if (!editingGeneralInstructions) {
            setGeneralInstructions(instructionsRes.data.instructions || '');
          }
        } catch (err) {
          console.error('Error loading principles:', err);
          // Keep existing data on error
        }
      } else if (activeTab === 'team') {
        const res = await axios.get('/api/team');
        setTeamMembers(res.data);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const handleStartOrchestrator = async () => {
    try {
      // Auto-start worker if not running
      if (!workerRunning) {
        const workerResponse = await axios.post('/api/orchestrator/continuous-execution/start-worker');
        if (!workerResponse.data.success) {
          alert('Failed to start worker: ' + (workerResponse.data.message || 'Unknown error'));
          return;
        }
      }

      // Start all principles that are under 100%
      await axios.post('/api/orchestrator/continuous-execution/start-all');
      loadData();
    } catch (error: any) {
      alert('Error: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleStopOrchestrator = async () => {
    try {
      // Stop all running principles
      await axios.post('/api/orchestrator/continuous-execution/stop-all');
      loadData();
    } catch (error: any) {
      alert('Error: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleAddPrinciple = async () => {
    if (!newPrinciple.title || !newPrinciple.description) {
      alert('Title and description are required');
      return;
    }
    try {
      await axios.post('/api/principles', newPrinciple);
      setNewPrinciple({ title: '', description: '', category: '' });
      loadData();
    } catch (error: any) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleUpdatePrinciple = async () => {
    if (!editingPrinciple) return;
    try {
      await axios.put(`/api/principles/${editingPrinciple.id}`, {
        name: editingPrinciple.name,
        description: editingPrinciple.description,
        percentage: editingPrinciple.percentage
      });
      setEditingPrinciple(null);
      loadData();
    } catch (error: any) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeletePrinciple = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this principle?')) return;
    try {
      await axios.delete(`/api/principles/${id}`);
      loadData();
    } catch (error: any) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleAddTeamMember = async () => {
    if (!newMember.name || !newMember.role) {
      alert('Name and role are required');
      return;
    }
    try {
      await axios.post('/api/team-members', newMember);
      setNewMember({ name: '', role: '' });
      loadData();
    } catch (error: any) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleUpdateTeamMember = async () => {
    if (!editingMember) return;
    try {
      await axios.put(`/api/team-members/${editingMember.filename}`, {
        role: editingMember.role
      });
      setEditingMember(null);
      loadData();
    } catch (error: any) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const toggleTeamMember = (teamMemberKey: string) => {
    const newExpanded = new Set(expandedTeamMembers);
    if (newExpanded.has(teamMemberKey)) {
      newExpanded.delete(teamMemberKey);
    } else {
      newExpanded.add(teamMemberKey);
    }
    setExpandedTeamMembers(newExpanded);
  };

  const groupPrinciplesByTeamMember = () => {
    const grouped: { [key: string]: any } = {};
    principles.forEach((principle) => {
      const key = principle.team_member_key || 'unknown';
      if (!grouped[key]) {
        grouped[key] = {
          name: principle.team_member,
          role: principle.team_member_role,
          key: key,
          principles: []
        };
      }
      grouped[key].principles.push(principle);
    });
    return Object.values(grouped);
  };

  const handleDeleteTeamMember = async (filename: string) => {
    if (!window.confirm('Are you sure you want to delete this team member?')) return;
    try {
      await axios.delete(`/api/team-members/${filename}`);
      loadData();
    } catch (error: any) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handlePlayPrinciple = async (member_id: string, principle_id: string) => {
    const key = `${member_id}.${principle_id}`;

    try {
      console.log(`[Play] Starting principle: ${key}`);

      // Mark as pending
      setPendingActions(prev => new Set(prev).add(key));

      // Optimistic UI update
      const updatedState = { ...continuousExecution };
      if (!updatedState.running_principles) {
        updatedState.running_principles = {};
      }
      updatedState.running_principles[key] = {
        member_id,
        principle_id,
        started_at: new Date().toISOString(),
        last_run: null,
        run_count: 0,
        last_output: null
      };
      setContinuousExecution(updatedState);

      // Auto-start worker if not running
      if (!workerRunning) {
        console.log(`[Play] Auto-starting worker`);
        const workerResponse = await axios.post('/api/orchestrator/continuous-execution/start-worker');
        console.log(`[Play] Worker response:`, workerResponse.data);
        if (!workerResponse.data.success) {
          alert('Failed to start worker: ' + (workerResponse.data.message || 'Unknown error'));
          setPendingActions(prev => { const next = new Set(prev); next.delete(key); return next; });
          loadData(); // Revert on error
          return;
        }
      }

      console.log(`[Play] Calling API to start principle`);
      const response = await axios.post('/api/orchestrator/continuous-execution/start', {
        member_id,
        principle_id
      });
      console.log(`[Play] API response:`, response.data);

      // Remove from pending and refresh
      setPendingActions(prev => { const next = new Set(prev); next.delete(key); return next; });
      await loadData(); // Refresh to confirm server state
    } catch (error: any) {
      console.error(`[Play] Error:`, error);
      alert('Error: ' + (error.response?.data?.detail || error.message));
      setPendingActions(prev => { const next = new Set(prev); next.delete(key); return next; });
      loadData(); // Revert on error
    }
  };

  const handlePausePrinciple = async (member_id: string, principle_id: string) => {
    const key = `${member_id}.${principle_id}`;

    try {
      console.log(`[Pause] Pausing principle: ${key}`);

      // Mark as pending
      setPendingActions(prev => new Set(prev).add(key));

      // Optimistic UI update
      const updatedState = { ...continuousExecution };
      if (updatedState.running_principles && updatedState.running_principles[key]) {
        delete updatedState.running_principles[key];
      }
      if (!updatedState.paused_principles) {
        updatedState.paused_principles = [];
      }
      if (!updatedState.paused_principles.includes(key)) {
        updatedState.paused_principles.push(key);
      }
      setContinuousExecution(updatedState);

      console.log(`[Pause] Calling API to pause principle`);
      const response = await axios.post('/api/orchestrator/continuous-execution/pause', {
        member_id,
        principle_id
      });
      console.log(`[Pause] API response:`, response.data);

      // Remove from pending and refresh
      setPendingActions(prev => { const next = new Set(prev); next.delete(key); return next; });
      await loadData(); // Refresh to confirm server state
    } catch (error: any) {
      console.error(`[Pause] Error:`, error);
      alert('Error: ' + (error.response?.data?.detail || error.message));
      setPendingActions(prev => { const next = new Set(prev); next.delete(key); return next; });
      loadData(); // Revert on error
    }
  };

  const handleStartWorker = async () => {
    try {
      const response = await axios.post('/api/orchestrator/continuous-execution/start-worker');
      if (response.data.success) {
        loadData();
      } else {
        alert(response.data.message || 'Failed to start worker');
      }
    } catch (error: any) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleStopWorker = async () => {
    try {
      const response = await axios.post('/api/orchestrator/continuous-execution/stop-worker');
      if (response.data.success) {
        loadData();
      } else {
        alert(response.data.message || 'Failed to stop worker');
      }
    } catch (error: any) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleSaveGeneralInstructions = async () => {
    try {
      await axios.put('/api/orchestrator/general-instructions', {
        instructions: generalInstructions
      });
      setEditingGeneralInstructions(false);
      alert('General instructions updated successfully!');
    } catch (error: any) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  return (
    <div style={{ backgroundColor: theme.background }}>
      {/* Action Bar */}
      <div style={{
        backgroundColor: theme.surface,
        borderBottom: `1px solid ${theme.border}`,
        padding: '12px 16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px',
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '18px', color: theme.text }}>Management</h1>
          <p style={{ margin: '4px 0 0 0', color: theme.textSecondary, fontSize: '13px' }}>
            Orchestrator, Principles, and Team Management
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="management-tabs" style={{
        backgroundColor: theme.surface,
        borderBottom: `1px solid ${theme.border}`,
        padding: '0 16px',
        display: 'flex',
        gap: '16px',
        overflowX: 'auto',
      }}>
        <button
          onClick={() => setActiveTab('principles')}
          style={{
            padding: '16px 0',
            backgroundColor: 'transparent',
            border: 'none',
            borderBottom: activeTab === 'principles' ? `2px solid ${theme.primary}` : '2px solid transparent',
            color: activeTab === 'principles' ? theme.primary : theme.textSecondary,
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
          }}
        >
          Principles
        </button>
        <button
          onClick={() => setActiveTab('orchestrator')}
          style={{
            padding: '16px 0',
            backgroundColor: 'transparent',
            border: 'none',
            borderBottom: activeTab === 'orchestrator' ? `2px solid ${theme.primary}` : '2px solid transparent',
            color: activeTab === 'orchestrator' ? theme.primary : theme.textSecondary,
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
          }}
        >
          Orchestrator Status
        </button>
        <button
          onClick={() => setActiveTab('team')}
          style={{
            padding: '16px 0',
            backgroundColor: 'transparent',
            border: 'none',
            borderBottom: activeTab === 'team' ? `2px solid ${theme.primary}` : '2px solid transparent',
            color: activeTab === 'team' ? theme.primary : theme.textSecondary,
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
          }}
        >
          Team Members
        </button>
      </div>

      {/* Content */}
      <div style={{ padding: '16px' }}>
        {activeTab === 'principles' && (
          <div>
            {/* General Instructions */}
            <div style={{
              backgroundColor: theme.surface,
              borderRadius: '12px',
              padding: '24px',
              marginBottom: '24px',
              boxShadow: isDark ? '0 1px 3px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.1)',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h2 style={{ margin: 0, color: theme.text }}>General Instructions for All Principles</h2>
                {!editingGeneralInstructions && (
                  <button
                    onClick={() => setEditingGeneralInstructions(true)}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: theme.primary,
                      color: '#fff',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                    }}
                  >
                    Edit
                  </button>
                )}
              </div>
              {editingGeneralInstructions ? (
                <div>
                  <textarea
                    value={generalInstructions}
                    onChange={(e) => setGeneralInstructions(e.target.value)}
                    rows={4}
                    placeholder="Enter instructions that will be prepended to every principle prompt..."
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: `1px solid ${theme.border}`,
                      backgroundColor: theme.background,
                      color: theme.text,
                      borderRadius: '6px',
                      fontFamily: 'inherit',
                      fontSize: '14px',
                      resize: 'vertical',
                    }}
                  />
                  <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                    <button
                      onClick={handleSaveGeneralInstructions}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#10b981',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                      }}
                    >
                      Save
                    </button>
                    <button
                      onClick={() => {
                        setEditingGeneralInstructions(false);
                        loadData(); // Reload to reset changes
                      }}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: theme.surfaceHover,
                        color: theme.text,
                        border: `1px solid ${theme.border}`,
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <p style={{ margin: 0, color: theme.textSecondary, fontSize: '14px', fontStyle: generalInstructions ? 'normal' : 'italic' }}>
                  {generalInstructions || 'No general instructions set. Click Edit to add instructions that will be included in every principle execution.'}
                </p>
              )}
            </div>

            {/* Add New Principle */}
            <div style={{
              backgroundColor: theme.surface,
              borderRadius: '12px',
              padding: '24px',
              marginBottom: '24px',
              boxShadow: isDark ? '0 1px 3px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.1)',
            }}>
              <h2 style={{ margin: '0 0 16px 0', color: theme.text }}>Add New Principle</h2>
              <div style={{ display: 'grid', gap: '12px' }}>
                <input
                  type="text"
                  placeholder="Title"
                  value={newPrinciple.title}
                  onChange={(e) => setNewPrinciple({ ...newPrinciple, title: e.target.value })}
                  style={{
                    padding: '12px',
                    border: `1px solid ${theme.border}`,
                    backgroundColor: theme.background,
                    color: theme.text,
                    borderRadius: '6px',
                  }}
                />
                <textarea
                  placeholder="Description"
                  value={newPrinciple.description}
                  onChange={(e) => setNewPrinciple({ ...newPrinciple, description: e.target.value })}
                  rows={3}
                  style={{
                    padding: '12px',
                    border: `1px solid ${theme.border}`,
                    backgroundColor: theme.background,
                    color: theme.text,
                    borderRadius: '6px',
                    fontFamily: 'inherit',
                  }}
                />
                <input
                  type="text"
                  placeholder="Category (optional)"
                  value={newPrinciple.category}
                  onChange={(e) => setNewPrinciple({ ...newPrinciple, category: e.target.value })}
                  style={{
                    padding: '12px',
                    border: `1px solid ${theme.border}`,
                    backgroundColor: theme.background,
                    color: theme.text,
                    borderRadius: '6px',
                  }}
                />
                <button
                  onClick={handleAddPrinciple}
                  style={{
                    padding: '12px',
                    backgroundColor: theme.primary,
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '500',
                  }}
                >
                  Add Principle
                </button>
              </div>
            </div>

            {/* Principles List - Grouped by Team Member */}
            <div style={{
              backgroundColor: theme.surface,
              borderRadius: '12px',
              padding: '24px',
              boxShadow: isDark ? '0 1px 3px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.1)',
            }}>
              <h2 style={{ margin: '0 0 16px 0', color: theme.text }}>
                Principles ({principles.length} total)
              </h2>
              {groupPrinciplesByTeamMember().map((teamGroup: any) => {
                const isExpanded = expandedTeamMembers.has(teamGroup.key);
                const avgPercentage = teamGroup.principles.reduce((sum: number, p: any) => sum + (p.percentage || 0), 0) / teamGroup.principles.length;

                return (
                  <div
                    key={teamGroup.key}
                    style={{
                      marginBottom: '16px',
                      backgroundColor: theme.background,
                      borderRadius: '8px',
                      border: `1px solid ${theme.border}`,
                      overflow: 'hidden',
                    }}
                  >
                    {/* Team Member Header */}
                    <div
                      onClick={() => toggleTeamMember(teamGroup.key)}
                      style={{
                        padding: '16px',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        backgroundColor: isExpanded ? theme.surfaceHover : theme.background,
                        transition: 'background-color 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        if (!isExpanded) e.currentTarget.style.backgroundColor = theme.surfaceHover;
                      }}
                      onMouseLeave={(e) => {
                        if (!isExpanded) e.currentTarget.style.backgroundColor = theme.background;
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <h3 style={{ margin: '0 0 4px 0', color: theme.text, fontSize: '16px' }}>
                          {isExpanded ? '▼' : '▶'} {teamGroup.name}
                        </h3>
                        <p style={{ margin: 0, color: theme.textSecondary, fontSize: '13px' }}>
                          {teamGroup.role} • {teamGroup.principles.length} principles
                        </p>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                          padding: '6px 12px',
                          borderRadius: '12px',
                          backgroundColor: avgPercentage >= 75 ? '#10b98120' : avgPercentage >= 50 ? '#f59e0b20' : '#ef444420',
                          color: avgPercentage >= 75 ? '#10b981' : avgPercentage >= 50 ? '#f59e0b' : '#ef4444',
                          fontSize: '13px',
                          fontWeight: '600',
                        }}>
                          {Math.round(avgPercentage)}% avg
                        </div>
                      </div>
                    </div>

                    {/* Principles (when expanded) */}
                    {isExpanded && (
                      <div style={{ padding: '0 16px 16px 16px' }}>
                        {teamGroup.principles.map((principle: any) => (
                          <div
                            key={principle.id}
                            style={{
                              padding: '12px',
                              marginTop: '12px',
                              backgroundColor: theme.surface,
                              borderRadius: '6px',
                              border: `1px solid ${theme.border}`,
                            }}
                          >
                            {editingPrinciple?.id === principle.id ? (
                              // Edit mode
                              <div style={{ display: 'grid', gap: '12px' }}>
                                <div>
                                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: theme.textSecondary }}>
                                    Name
                                  </label>
                                  <input
                                    type="text"
                                    value={editingPrinciple.name}
                                    onChange={(e) => setEditingPrinciple({ ...editingPrinciple, name: e.target.value })}
                                    style={{
                                      width: '100%',
                                      padding: '8px',
                                      border: `1px solid ${theme.border}`,
                                      backgroundColor: theme.background,
                                      color: theme.text,
                                      borderRadius: '4px',
                                      fontSize: '14px',
                                    }}
                                  />
                                </div>
                                <div>
                                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: theme.textSecondary }}>
                                    Description
                                  </label>
                                  <textarea
                                    value={editingPrinciple.description}
                                    onChange={(e) => setEditingPrinciple({ ...editingPrinciple, description: e.target.value })}
                                    rows={3}
                                    style={{
                                      width: '100%',
                                      padding: '8px',
                                      border: `1px solid ${theme.border}`,
                                      backgroundColor: theme.background,
                                      color: theme.text,
                                      borderRadius: '4px',
                                      fontSize: '12px',
                                      fontFamily: 'inherit',
                                      resize: 'vertical',
                                    }}
                                  />
                                </div>
                                <div>
                                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: theme.textSecondary }}>
                                    Percentage (0-100)
                                  </label>
                                  <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    value={editingPrinciple.percentage || 0}
                                    onChange={(e) => setEditingPrinciple({ ...editingPrinciple, percentage: Math.min(100, Math.max(0, parseInt(e.target.value) || 0)) })}
                                    style={{
                                      width: '100px',
                                      padding: '8px',
                                      border: `1px solid ${theme.border}`,
                                      backgroundColor: theme.background,
                                      color: theme.text,
                                      borderRadius: '4px',
                                      fontSize: '14px',
                                    }}
                                  />
                                </div>
                                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                  <button
                                    onClick={handleUpdatePrinciple}
                                    style={{
                                      padding: '6px 12px',
                                      backgroundColor: '#10b981',
                                      color: '#fff',
                                      border: 'none',
                                      borderRadius: '4px',
                                      cursor: 'pointer',
                                      fontSize: '12px',
                                      fontWeight: '500',
                                    }}
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={() => setEditingPrinciple(null)}
                                    style={{
                                      padding: '6px 12px',
                                      backgroundColor: theme.surfaceHover,
                                      color: theme.text,
                                      border: `1px solid ${theme.border}`,
                                      borderRadius: '4px',
                                      cursor: 'pointer',
                                      fontSize: '12px',
                                    }}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              // View mode - clickable card
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                                <div
                                  onClick={() => setEditingPrinciple(principle)}
                                  style={{
                                    flex: 1,
                                    cursor: 'pointer',
                                    transition: 'opacity 0.2s',
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.opacity = '0.7';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.opacity = '1';
                                  }}
                                >
                                  <h4 style={{ margin: '0 0 4px 0', color: theme.text, fontSize: '14px' }}>
                                    {principle.name}
                                  </h4>
                                  <p style={{ margin: '0 0 8px 0', color: theme.textSecondary, fontSize: '12px' }}>
                                    {principle.description}
                                  </p>
                                  {principle.status_description !== 'Not yet evaluated' && (
                                    <p style={{ margin: 0, color: theme.textSecondary, fontSize: '11px', fontStyle: 'italic' }}>
                                      Status: {principle.status_description}
                                    </p>
                                  )}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'start', gap: '8px', marginLeft: '12px' }}>
                                  <div style={{
                                    padding: '4px 10px',
                                    borderRadius: '10px',
                                    backgroundColor: principle.percentage >= 75 ? '#10b98120' : principle.percentage >= 50 ? '#f59e0b20' : '#ef444420',
                                    color: principle.percentage >= 75 ? '#10b981' : principle.percentage >= 50 ? '#f59e0b' : '#ef4444',
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    whiteSpace: 'nowrap',
                                  }}>
                                    {principle.percentage}%
                                  </div>
                                  {(() => {
                                    const key = `${principle.team_member_key}.${principle.id}`;
                                    const isRunning = continuousExecution.running_principles?.[key] !== undefined;

                                    return isRunning ? (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handlePausePrinciple(principle.team_member_key, principle.id);
                                        }}
                                        style={{
                                          padding: '4px 12px',
                                          backgroundColor: '#ef4444',
                                          color: '#fff',
                                          border: 'none',
                                          borderRadius: '6px',
                                          cursor: 'pointer',
                                          fontSize: '12px',
                                          fontWeight: '500',
                                          whiteSpace: 'nowrap',
                                          transition: 'background-color 0.2s',
                                        }}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.backgroundColor = '#dc2626';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.backgroundColor = '#ef4444';
                                        }}
                                      >
                                        ⏸ Pause
                                      </button>
                                    ) : (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handlePlayPrinciple(principle.team_member_key, principle.id);
                                        }}
                                        style={{
                                          padding: '4px 12px',
                                          backgroundColor: '#10b981',
                                          color: '#fff',
                                          border: 'none',
                                          borderRadius: '6px',
                                          cursor: 'pointer',
                                          fontSize: '12px',
                                          fontWeight: '500',
                                          whiteSpace: 'nowrap',
                                          transition: 'background-color 0.2s',
                                        }}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.backgroundColor = '#059669';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.backgroundColor = '#10b981';
                                        }}
                                      >
                                        ▶ Play
                                      </button>
                                    );
                                  })()}
                                  {(() => {
                                    const key = `${principle.team_member_key}.${principle.id}`;
                                    const isRunning = continuousExecution.running_principles?.[key] !== undefined;

                                    return isRunning && (
                                      <span style={{ marginLeft: '4px', color: '#10b981', fontSize: '12px', display: 'flex', alignItems: 'center' }}>
                                        🔄 Running...
                                      </span>
                                    );
                                  })()}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {activeTab === 'orchestrator' && (
          <div>
            <div style={{
              backgroundColor: theme.surface,
              borderRadius: '12px',
              padding: '24px',
              marginBottom: '24px',
              boxShadow: isDark ? '0 1px 3px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.1)',
            }}>
              <h2 style={{ margin: '0 0 16px 0', color: theme.text }}>Orchestrator Status</h2>
              {orchestratorStatus && (
                <div>
                  <div style={{ marginBottom: '20px' }}>
                    <p style={{ color: theme.textSecondary, marginBottom: '8px' }}>
                      Status: <strong style={{ color: orchestratorStatus.monitor_pid ? theme.success : theme.error }}>
                        {orchestratorStatus.monitor_pid ? 'Running' : 'Stopped'}
                      </strong>
                    </p>
                    {orchestratorStatus.monitor_pid && (
                      <>
                        <p style={{ color: theme.textSecondary, marginBottom: '4px', fontSize: '14px' }}>
                          PID: {orchestratorStatus.monitor_pid}
                        </p>
                        <p style={{ color: theme.textSecondary, marginBottom: '4px', fontSize: '14px' }}>
                          Current Team Member: <strong>{orchestratorStatus.current_member || 'N/A'}</strong>
                        </p>
                        <p style={{ color: theme.textSecondary, marginBottom: '4px', fontSize: '14px' }}>
                          Iteration Count: {orchestratorStatus.iteration_count || 0}
                        </p>
                        {orchestratorStatus.job_queue && (
                          <>
                            <p style={{ color: theme.textSecondary, marginBottom: '4px', fontSize: '14px' }}>
                              Active Jobs: {orchestratorStatus.job_queue.active_jobs || 0} / {orchestratorStatus.job_queue.max_concurrent || 0}
                            </p>
                            <p style={{ color: theme.textSecondary, marginBottom: '4px', fontSize: '14px' }}>
                              Queue Size: {orchestratorStatus.job_queue.queue_size || 0}
                            </p>
                          </>
                        )}
                      </>
                    )}
                  </div>

                  {/* Continuous Execution Status */}
                  <div style={{
                    marginBottom: '20px',
                    paddingTop: '20px',
                    borderTop: `1px solid ${theme.border}`
                  }}>
                    <h3 style={{ margin: '0 0 12px 0', color: theme.text, fontSize: '16px' }}>
                      Continuous Execution
                    </h3>
                    <p style={{ color: theme.textSecondary, marginBottom: '8px', fontSize: '14px' }}>
                      Worker Status: <strong style={{ color: workerRunning ? theme.success : theme.error }}>
                        {workerRunning ? '🟢 Running' : '🔴 Stopped'}
                      </strong>
                    </p>
                    <p style={{ color: theme.textSecondary, marginBottom: '8px', fontSize: '14px' }}>
                      Running Principles: <strong style={{ color: theme.success }}>
                        {Object.keys(continuousExecution.running_principles || {}).length}
                      </strong>
                    </p>
                    <p style={{ color: theme.textSecondary, marginBottom: '8px', fontSize: '14px' }}>
                      Paused Principles: <strong style={{ color: theme.warning }}>
                        {(continuousExecution.paused_principles || []).length}
                      </strong>
                    </p>

                    {/* Worker Control Buttons */}
                    <div style={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
                      <button
                        onClick={handleStartWorker}
                        disabled={workerRunning}
                        style={{
                          padding: '6px 16px',
                          backgroundColor: workerRunning ? theme.surfaceHover : '#10b981',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: workerRunning ? 'not-allowed' : 'pointer',
                          opacity: workerRunning ? 0.5 : 1,
                          fontSize: '12px',
                        }}
                      >
                        ▶ Start Worker
                      </button>
                      <button
                        onClick={handleStopWorker}
                        disabled={!workerRunning}
                        style={{
                          padding: '6px 16px',
                          backgroundColor: !workerRunning ? theme.surfaceHover : '#ef4444',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: !workerRunning ? 'not-allowed' : 'pointer',
                          opacity: !workerRunning ? 0.5 : 1,
                          fontSize: '12px',
                        }}
                      >
                        ■ Stop Worker
                      </button>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                      onClick={handleStartOrchestrator}
                      style={{
                        padding: '10px 20px',
                        backgroundColor: theme.success,
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                      }}
                    >
                      ▶ Start All &lt;100%
                    </button>
                    <button
                      onClick={handleStopOrchestrator}
                      style={{
                        padding: '10px 20px',
                        backgroundColor: theme.error,
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                      }}
                    >
                      ■ Stop All
                    </button>
                  </div>
                </div>
              )}
            </div>

            {iterationState && Object.keys(iterationState).length > 0 && (
              <div style={{
                backgroundColor: theme.surface,
                borderRadius: '12px',
                padding: '24px',
                boxShadow: isDark ? '0 1px 3px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.1)',
              }}>
                <h2 style={{ margin: '0 0 16px 0', color: theme.text }}>Team Member Rotation</h2>
                {iterationState.rotation_order && (
                  <div style={{ display: 'grid', gap: '8px' }}>
                    {iterationState.rotation_order.map((member: string, index: number) => (
                      <div
                        key={member}
                        style={{
                          padding: '12px',
                          backgroundColor: member === iterationState.current_member ? theme.primary + '20' : theme.background,
                          borderRadius: '6px',
                          border: member === iterationState.current_member ? `2px solid ${theme.primary}` : `1px solid ${theme.border}`,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <span style={{
                            color: theme.textSecondary,
                            fontSize: '12px',
                            minWidth: '20px',
                          }}>
                            #{index + 1}
                          </span>
                          <span style={{
                            color: theme.text,
                            fontWeight: member === iterationState.current_member ? '600' : '400',
                            fontSize: '14px',
                          }}>
                            {member.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                          </span>
                        </div>
                        {member === iterationState.current_member && (
                          <span style={{
                            padding: '4px 8px',
                            backgroundColor: theme.primary,
                            color: '#fff',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: '600',
                          }}>
                            CURRENT
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {iterationState.started_at && (
                  <div style={{ marginTop: '16px', padding: '12px', backgroundColor: theme.background, borderRadius: '6px' }}>
                    <p style={{ margin: '0 0 4px 0', color: theme.textSecondary, fontSize: '13px' }}>
                      Started: {new Date(iterationState.started_at).toLocaleString()}
                    </p>
                    {iterationState.last_rotation_time && (
                      <p style={{ margin: 0, color: theme.textSecondary, fontSize: '13px' }}>
                        Last Rotation: {new Date(iterationState.last_rotation_time).toLocaleString()}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'team' && (
          <div>
            {/* Add New Team Member */}
            <div style={{
              backgroundColor: theme.surface,
              borderRadius: '12px',
              padding: '24px',
              marginBottom: '24px',
              boxShadow: isDark ? '0 1px 3px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.1)',
            }}>
              <h2 style={{ margin: '0 0 16px 0', color: theme.text }}>Add New Team Member</h2>
              <div style={{ display: 'grid', gap: '12px' }}>
                <input
                  type="text"
                  placeholder="Name (e.g., John Doe)"
                  value={newMember.name}
                  onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
                  style={{
                    padding: '12px',
                    border: `1px solid ${theme.border}`,
                    backgroundColor: theme.background,
                    color: theme.text,
                    borderRadius: '6px',
                  }}
                />
                <input
                  type="text"
                  placeholder="Role (e.g., Frontend Developer)"
                  value={newMember.role}
                  onChange={(e) => setNewMember({ ...newMember, role: e.target.value })}
                  style={{
                    padding: '12px',
                    border: `1px solid ${theme.border}`,
                    backgroundColor: theme.background,
                    color: theme.text,
                    borderRadius: '6px',
                  }}
                />
                <button
                  onClick={handleAddTeamMember}
                  style={{
                    padding: '12px',
                    backgroundColor: theme.primary,
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '500',
                  }}
                >
                  Add Team Member
                </button>
              </div>
            </div>

            {/* Team Members List */}
            <div style={{
              backgroundColor: theme.surface,
              borderRadius: '12px',
              padding: '24px',
              boxShadow: isDark ? '0 1px 3px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.1)',
            }}>
              <h2 style={{ margin: '0 0 16px 0', color: theme.text }}>
                Team Members ({teamMembers.length})
              </h2>
              <div style={{ display: 'grid', gap: '12px' }}>
                {teamMembers.map((member) => (
                  <div
                    key={member.name}
                    style={{
                      padding: '16px',
                      backgroundColor: theme.background,
                      borderRadius: '8px',
                      border: `1px solid ${theme.border}`,
                    }}
                  >
                    {editingMember?.name === member.name ? (
                      <div style={{ display: 'grid', gap: '12px' }}>
                        <input
                          type="text"
                          value={editingMember.role}
                          onChange={(e) => setEditingMember({ ...editingMember, role: e.target.value })}
                          placeholder="Job Title"
                          style={{
                            padding: '10px',
                            border: `1px solid ${theme.border}`,
                            backgroundColor: theme.surface,
                            color: theme.text,
                            borderRadius: '6px',
                          }}
                        />
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={handleUpdateTeamMember}
                            style={{
                              padding: '8px 16px',
                              backgroundColor: theme.success,
                              color: '#fff',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                            }}
                          >
                            Save
                          </button>
                          <button
                            onClick={() => setEditingMember(null)}
                            style={{
                              padding: '8px 16px',
                              backgroundColor: theme.surfaceHover,
                              color: theme.text,
                              border: `1px solid ${theme.border}`,
                              borderRadius: '6px',
                              cursor: 'pointer',
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <h3 style={{ margin: '0 0 4px 0', color: theme.text }}>{member.name}</h3>
                          <p style={{ margin: 0, color: theme.textSecondary, fontSize: '14px' }}>
                            {member.role}
                          </p>
                          <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: theme.textSecondary }}>
                            Tasks: {member.queued_tasks} queued, {member.in_progress_tasks} active, {member.completed_tasks} done{member.failed_tasks > 0 && `, ${member.failed_tasks} failed`}
                          </p>
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => setEditingMember(member)}
                            style={{
                              padding: '8px 16px',
                              backgroundColor: theme.primary,
                              color: '#fff',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                            }}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteTeamMember(member.filename)}
                            style={{
                              padding: '8px 16px',
                              backgroundColor: theme.error,
                              color: '#fff',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Management;
